/* eslint-disable no-bitwise */
import {useCallback, useMemo} from 'react'
import {requestData} from 'services'
import {ObjectMapRef} from 'types/commons'
import {ListHandlerUseCaseFunction} from 'types/components'
import {
  DropboxUploadMapData,
  DropboxUploadMeta,
  DropboxUploadUseCaseType,
} from 'types/modules'
import {useListHandler} from 'utils/components'
import {
  getDynamicCompressQuality,
  handleUploadSingleFile,
  inflateImageToSize,
} from 'utils/commons'
import {getCompressedImage, getFileSignature} from './DropboxUploadFileHelper'

const FILE_UPLOAD_COMPRESS_MAX_SIZE = 2e6

const UPLOAD_RESPONSE_WARNING_DUPLICATE_MESSAGES = [
  'content duplicate',
  'original content duplicate',
]

export function useDropboxUploadUseCases(
  mapRef: ObjectMapRef<DropboxUploadMapData>,
) {
  const listHandler = useListHandler<
    File,
    DropboxUploadUseCaseType,
    DropboxUploadMeta
  >()
  const {map, setMapValue, updateMap} = mapRef

  const handleLoadFile = useCallback<
    ListHandlerUseCaseFunction<File, DropboxUploadMeta>
  >(async (file, meta) => {
    let uploadFile: File | Blob = file

    if (file.size > FILE_UPLOAD_COMPRESS_MAX_SIZE) {
      const quality = getDynamicCompressQuality(file)
      const compressedFile = await getCompressedImage(file, {
        quality,
        retainExif: true,
        convertSize: Infinity,
        mimeType: 'image/jpeg',
      })

      if (!compressedFile) return false

      if (compressedFile.size < file.size) {
        try {
          uploadFile = await inflateImageToSize(
            compressedFile,
            FILE_UPLOAD_COMPRESS_MAX_SIZE,
          )
        } catch (err) {
          return false
        }
      }
    }

    meta.current = {upload_file: uploadFile}

    return true
  }, [])

  const handlePresignUrl = useCallback<
    ListHandlerUseCaseFunction<File, DropboxUploadMeta>
  >(async (file, meta) => {
    if (!meta.current) return false

    const {upload_file} = meta.current
    const signatureOriginal = await getFileSignature(file)
    const signature =
      file === upload_file
        ? signatureOriginal
        : await getFileSignature(upload_file)

    if (!signatureOriginal || !signature) return false

    const response = await requestData('giftshop_upload_link', {
      data: {content: signature, original_content: signatureOriginal},
    })

    /** Handle client side error */
    if (typeof response === 'string') return false

    /** Handle duplicate warning */
    if (
      response.status === 400 &&
      UPLOAD_RESPONSE_WARNING_DUPLICATE_MESSAGES.find(
        (message) => response.data.detail === message,
      )
    ) {
      return 'warning'
    }

    if (response.status !== 200) return false

    const {content, content_url} = response.data

    meta.current = {
      ...meta.current,
      signature,
      signatureOriginal,
      content_enc: content,
      content_url,
    }

    return true
  }, [])

  const handleUploadFile = useCallback<
    ListHandlerUseCaseFunction<File, DropboxUploadMeta>
  >(async (_, meta) => {
    if (!meta.current) return false

    const {upload_file, content_url} = meta.current

    if (!content_url) return false

    const isSuccess = await handleUploadSingleFile(
      upload_file,
      content_url,
      upload_file.type,
    )

    return isSuccess
  }, [])

  const handleUploadProperties = useCallback<
    ListHandlerUseCaseFunction<File, DropboxUploadMeta>
  >(async (file, meta) => {
    if (!meta.current) return false

    const {content_enc, content_url, upload_file} = meta.current!

    if (!content_enc || !content_url) return false

    const unix = Math.floor(Date.now() / 1000)
    const isCompressed = file !== upload_file

    const response = await requestData('giftshop_upload_submit', {
      data: {
        content: content_enc,
        content_url,
        title: file.name,
        unix_original_at: unix,
        is_hires_compress: isCompressed,
      },
    })

    return typeof response === 'object' && response.status === 202
  }, [])

  const registerUseCase = useCallback(() => {
    listHandler.registerUseCase('load-file', handleLoadFile)
    listHandler.registerUseCase('presign-url', handlePresignUrl)
    listHandler.registerUseCase('upload-cloud', handleUploadFile)
    listHandler.registerUseCase('upload-properties', handleUploadProperties)
  }, [
    listHandler,
    handleLoadFile,
    handlePresignUrl,
    handleUploadFile,
    handleUploadProperties,
  ])

  const handleLoadFiles = useCallback(
    async (files: File[]) => {
      listHandler.runUseCases(files, {
        onStart: (options) => {
          setMapValue({
            name: undefined,
            isRunning: true,
            index: -1,
            total: options.total,
            totalSuccess: options.totalSuccess,
            totalFailed: options.totalFailed,
            totalWarning: options.totalWarning,
            statuses: [...options.statuses],
            listSuccess: [],
            listFailed: [],
            listWarning: [],
          })
          updateMap()
        },
        onStartUseCase: (options) => {
          setMapValue({name: options.name})
          updateMap()
        },
        onStartItem: (options) => {
          setMapValue({
            index: options.index,
            total: options.total,
            totalSuccess: options.totalSuccess,
            totalFailed: options.totalFailed,
            totalWarning: options.totalWarning,
            statuses: [...options.statuses],
          })
          updateMap()
        },
        onResult: (options) => {
          setMapValue({
            name: undefined,
            isRunning: false,
            total: options.total,
            totalSuccess: options.totalSuccess,
            totalFailed: options.totalFailed,
            totalWarning: options.totalWarning,
            statuses: [...options.statuses],
            listSuccess: [...options.listSuccess],
            listFailed: [...options.listFailed],
            listWarning: [...options.listWarning],
            listAllSuccess: [...options.listSuccess],
          })
          updateMap()
        },
      })
    },
    [listHandler, setMapValue, updateMap],
  )

  const handleRetry = useCallback(() => {
    handleLoadFiles(map.current.listFailed)
  }, [map, handleLoadFiles])

  return useMemo(
    () => ({
      registerUseCase,
      onLoadFiles: handleLoadFiles,
      onRetry: handleRetry,
    }),
    [registerUseCase, handleLoadFiles, handleRetry],
  )
}
